export default {
	vinChecker: {
		title: `What connectivity services is your vehicle capable of`,
		description: `Find the information for your current or future car`,
		form: {
			title: `Enter your VIN number`,
			placeholder: `VIN`,
			tooltipTitle: `How do I find my VIN number?`,
			tooltip: `Your Vehicle Identification Number (VIN) is typically located on the dashboard, near the windshield on the driver's side. It may also be found on the driver's side door jamb or within the engine bay. For specific guidance, refer to your owner's manual or visit our website's FAQ section for detailed instructions.`,
			primaryCta: `Search`,
			note: `The connectivity services displayed on this page are limited to those permitted by local legislation in the country associated with this URL. Using these services in other locations may be restricted by national or local laws; therefore, customers should check their VIN on the website corresponding to their country of residence. As regulations may change, you should always verify that local laws allow the use of these services. Additionally, the availability of our services may depend on 2G/3G network coverage in your area. {{brand}} cannot guarantee service continuity or be held responsible if a country, region, or telecom operator discontinues 2G/3G network services.`
		},
		appLinksV2: {
			appName: `{{app}}`,
			cta: `Download {{app}} app`,
			description: `Please go to {{app}} app to activate your subscriptions`,
			title: `Smart Connectivity`,
			appleStoreUrl: `https://apps.apple.com/app/toyota-myt/id1617623127`,
			playStoreUrl: `https://play.google.com/store/apps/details?id=com.toyota.oneapp.eu`
		}
	},
	csFeatures: {
		vinCheckerCSFeaturesLabels: {
			title: `Connected Services`,
			description: `The following connected services are available to you through the {{app}} App.`,
			note: `Please note that all features are dependent on the model. Please consult the full information here`,
			servicesDisclaimer: `{{brand}} Connectivity Match displays the connected services available for your vehicle. These services are accessible through the {{app}} app, either as a trial or a paid subscription. The trial period may begin from the warranty date of your vehicle or the service launch date. Please note that the current status of your trial period or subscription packages is not shown on this page. To check the status, log in to the {{app}} app`,
			paidSubscription: `Paid subscription`,
			trialEligibility: {
				trialDays: `{{value}} day(s) trial`,
				trialMonths: `{{value}} month(s) trial`,
				trialYears: `{{value}} year(s) trial`
			}
		},
		vinCheckerCSFeaturesList: {
			servicesList: {
				stellantisConnectedServices: `Connected Services`,
				defaultService: `Default Service`,
				digitalKeyServices: `Digital Key Services`,
				remoteServices: `Remote Services`,
				standardServices: `Standard Services`
			},
			categoryList: {
				remoteCapabilities: `Remote Capabilities`,
				evCapabilities: `EV Capabilities`,
				drivingAnalytics: `Driving Analytics`,
				battery: `Battery`,
				locationCapabilities: `Location Capabilities`,
				fuel: `Fuel`,
				telemetry: `Telemetry`,
				miscellaneous: `Miscellaneous`,
				weHybrid: `WeHybrid`,
				vehicleDiagnostic: `Vehicle Diagnostic`,
				climateCapabilities: `Climate Capabilities`,
				vehicleStatus: `Vehicle Status`
			},
			subCategoryList: {
				vehicleStatus: `Vehicle Status`,
				advancedControls: `Advanced Controls`,
				basicRemoteClimateControl: `Basic Remote Climate Control`,
				doorLockUnlock: `Door Lock Unlock`,
				remoteHvacSettingSeatHeater: `Remote Hvac Setting - SeatHeater`,
				remoteHvacSettingSeatVentilation: `Remote Hvac Setting - SeatVentilation`,
				remoteHvacSettingWindshieldRrWindowDefrost: `Remote Hvac Setting - Windshield window defrost`,
				remoteHvacSettingSteeringWheelHeater: `Remote Hvac Setting - Steering wheel heater`,
				spotMyCar: `Spot My Car`,
				fullClimateControl: `Full Climate Control`
			},
			extendedCapabilityList: {
				stellantisClimateCapable: `Climate Capable`,
				dailyCharge: `Daily Charge`,
				stellantisVehicleStatusCapable: `Vehicle Status Capable`,
				drivePulse: `Drive Pulse`,
				electricPulse: `Electric Pulse`,
				hydrogenPulse: `Hydrogen Pulse`,
				hybridPulse: `Hybrid Pulse`,
				batteryStatus: `Battery Status`,
				lastParkedCapable: `Last Parked Capable`,
				fuelLevelAvailable: `Fuel Level Available`,
				fuelRangeAvailable: `Fuel Range Available`,
				telemetryCapable: `Telemetry Capable`,
				homeCharge: `Home Charge`,
				weHybridCapable: `WeHybrid Capable`,
				enhancedSecuritySystemCapable: `Enhanced Security System Capable`,
				weeklyCharge: `Weekly Charge`,
				nextCharge: `Next Charge`,
				frontDriverDoorOpenStatus: `Front Driver Door Open Status`,
				frontPassengerDoorOpenStatus: `Front Passenger Door Open Status`,
				rearDriverDoorOpenStatus: `Rear Driver Door Open Status`,
				rearPassengerDoorOpenStatus: `Rear Passenger Door Open Status`,
				frontDriverDoorLockStatus: `Front Driver Door Lock Status`,
				frontPassengerDoorLockStatus: `Front Passenger Door Lock Status`,
				rearDriverDoorLockStatus: `Rear Driver Door Lock Status`,
				rearPassengerDoorLockStatus: `Rear Passenger Door Lock Status`,
				frontDriverDoorWindowStatus: `Front Driver Door Window Status`,
				frontPassengerDoorWindowStatus: `Front Passenger Door Window Status`,
				rearDriverDoorWindowStatus: `Rear Driver Door Window Status`,
				rearPassengerDoorWindowStatus: `Rear Passenger Door Window Status`,
				rearHatchRearWindow: `Rear Hatch Rear Window`,
				moonroof: `Moon roof`,
				sunroof: `Sun roof`,
				vehicleStatus: `Vehicle Status`,
				lightStatus: `Light Status`,
				bonnetStatus: `Bonnet Status`,
				vehicleDiagnosticCapable: `Vehicle Diagnostic Capable`,
				privacy: `Privacy`,
				powerWindowsCapable: `Power Windows Capable`,
				buzzerCapable: `Buzzer Capable`,
				hazardCapable: `Hazard Capable`,
				trunkLockUnlockCapable: `Trunk Lock Unlock Capable`,
				lightsCapable: `Lights Capable`,
				hornCapable: `Horn Capable`,
				remoteEngineStartStop: `Remote Engine Start Stop`,
				equippedWithAlarm: `Equipped With Alarm`,
				climateCapable: `Climate Capable`,
				econnectClimateCapable: `Econnect Climate Capable`,
				acScheduling: `AC Scheduling`,
				frontPassengerSeatHeater: `Front Passenger Seat Heater`,
				frontDriverSeatHeater: `Front Driver Seat Heater`,
				rearDriverSeatHeater: `Rear Driver Seat Heater`,
				rearPassengerSeatHeater: `Rear Passenger Seat Heater`,
				frontDriverSeatVentilation: `Front Driver Seat Ventilation`,
				frontPassengerSeatVentilation: `Front Passenger Seat Ventilation`,
				rearDriverSeatVentilation: `Rear Driver Seat Ventilation`,
				rearPassengerSeatVentilation: `Rear Passenger Seat Ventilation`,
				steeringHeater: `Steering Heater`,
				mirrorHeater: `Mirror Heater`,
				frontDefogger: `Front Defogger`,
				rearDefogger: `Rear Defogger`,
				vehicleFinder: `Vehicle Finder`,
				doorLockUnlockCapable: `Door Lock Unlock Capable`,
				climateStartEngine: `Climate Start Engine`,
				h2FuelStation: `H2 Fuel Station`,
				myDestination: `My Destination`,
				evChargeStationsCapable: `EV Charge Stations Capable`,
				serviceHistory: `Service History`,
				digitalKey: `Digital Key`,
				dealerAppointment: `Dealer Appointment`
			}
		}
	},
	errorMessages: {
		invalidVin: `The VIN that you entered is invalid! Please try again!`,
		emptyVin: `Please enter VIN!`,
		noCSDataForVin: `*This VIN does not have connected services. Please check the default services or log in to see full information about your vehicle.`,
		fetchingError: `Something went wrong and we are not able to retrieve information about this vehicle, please try again later.`,
		nonExistingVin: `Our system did not find the VIN code you entered. Make sure that the VIN code is correct: see Where can I find the VIN number on the 'i' icon above. If the problem persists, your car may not be compatible with digital services.`,
		proaceError: `Search results for this model will be available soon — thank you for your patience.`
	}
};
